/**
 * @file Frontend utility — resolveUploadAssetUrl.
 */

import { getUploadOrigin } from "../config/apiConfig";

/**
 * Turn API-stored upload paths (`/uploads/...`) into absolute URLs for `<img src>` and `<a href>`
 * when the SPA and API are on different origins (typical CRA + Express).
 *
 * `REACT_APP_API_BASE_URL` is usually `http://host:5050/api`; static files are served at `http://host:5050/uploads`.
 */
function uploadOriginFromApiBase(apiBaseUrl?: string | null): string {
  return getUploadOrigin(apiBaseUrl);
}

type MaterialImageFields = {
  image?: string | null;
  imageUrl?: string | null;
  image_url?: string | null;
  material?: MaterialImageFields | null;
  expertProfile?: { material?: MaterialImageFields | null } | null;
};

function pickImageFromRecord(record: MaterialImageFields | null | undefined): string {
  if (!record) return "";
  for (const value of [record.image, record.imageUrl, record.image_url]) {
    const trimmed = String(value ?? "").trim();
    if (trimmed) return trimmed;
  }
  return "";
}

/** Map material catalog ids to stored upload paths (`/uploads/...`). */
export function buildCatalogImageById(catalogItems: unknown[]): Map<number, string> {
  const catalogImageById = new Map<number, string>();
  for (const item of catalogItems) {
    const id = Number((item as MaterialImageFields & { id?: number })?.id);
    const path = pickMaterialImagePath(item as MaterialImageFields);
    if (Number.isFinite(id) && path) {
      catalogImageById.set(id, path);
    }
  }
  return catalogImageById;
}

/** Prefer row image fields; fall back to full `/materials` catalog by id. */
export function resolveMaterialImageFromCatalog(
  material: (MaterialImageFields & { id?: number | null }) | null | undefined,
  catalogImageById?: Map<number, string>
): string {
  const direct = pickMaterialImagePath(material);
  if (direct) return direct;
  const id = Number(material?.id);
  if (Number.isFinite(id)) {
    return catalogImageById?.get(id) ?? "";
  }
  return "";
}

/** Normalize material image fields from API payloads (camelCase, snake_case, nested material). */
export function pickMaterialImagePath(
  material: MaterialImageFields | null | undefined
): string {
  if (!material) return "";
  for (const source of [material, material.material, material.expertProfile?.material]) {
    const path = pickImageFromRecord(source);
    if (path) return path;
  }
  return "";
}

/** Resolve catalog / assignment row id (`id`, `materialId`, or nested `material.id`). */
export function getMaterialRecordId(material: any): number | null {
  const raw =
    material?.material?.id ??
    material?.materialId ??
    material?.material_id ??
    material?.id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Exported function — normalize Expert Assigned Material. */
export function normalizeExpertAssignedMaterial(
  row: any,
  catalogImageById?: Map<number, string>
): { id: number; name: string; code: string; imageUrl: string | null } | null {
  const id = getMaterialRecordId(row);
  if (id == null) return null;

  const nested = row?.material;
  const name = String(nested?.name ?? row?.name ?? "").trim();
  const code = String(nested?.code ?? row?.code ?? "").trim();
  const imageUrl =
    pickMaterialImagePath(row) || catalogImageById?.get(id) || null;

  return { id, name, code, imageUrl };
}

/** Normalize `/expert/materials` payloads (array, single object, or `{ materials: [] }`). */
export function parseExpertMaterialsResponse(payload: unknown): any[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;
  if (Array.isArray(obj.materials)) return obj.materials;
  if (getMaterialRecordId(obj) != null || obj.material) return [obj];
  return [];
}

/**
 * Build assigned-material cards from expert rows + full `/materials` catalog.
 * Catalog records are preferred so `imageUrl` is always present when the material exists.
 */
export function buildExpertAssignedMaterialsList(
  expertRows: any[],
  catalogItems: any[]
): { id: number; name: string; code: string; imageUrl: string | null }[] {
  const catalogById = new Map<number, any>();
  for (const item of catalogItems) {
    const id = Number(item?.id);
    if (Number.isFinite(id)) catalogById.set(id, item);
  }

  const catalogImageById = new Map<number, string>();
  catalogById.forEach((item, id) => {
    const path = pickMaterialImagePath(item);
    if (path) catalogImageById.set(id, path);
  });

  const seen = new Set<number>();
  const result: { id: number; name: string; code: string; imageUrl: string | null }[] =
    [];

  for (const row of expertRows) {
    const materialId = getMaterialRecordId(row);
    if (materialId == null || seen.has(materialId)) continue;
    seen.add(materialId);

    const catalogItem = catalogById.get(materialId);
    if (catalogItem) {
      const name = String(
        catalogItem.name ?? row?.material?.name ?? row?.name ?? ""
      ).trim();
      if (!name) continue;
      result.push({
        id: materialId,
        name,
        code: String(
          catalogItem.code ?? row?.material?.code ?? row?.code ?? ""
        ).trim(),
        imageUrl:
          pickMaterialImagePath(catalogItem) ||
          pickMaterialImagePath(row) ||
          catalogImageById.get(materialId) ||
          null,
      });
      continue;
    }

    const normalized = normalizeExpertAssignedMaterial(row, catalogImageById);
    if (normalized?.name) result.push(normalized);
  }

  return result;
}

/** Exported function — resolve Upload Asset Url. */
export function resolveUploadAssetUrl(
  pathOrUrl: string | undefined | null,
  apiBaseUrl?: string | null
): string {
  const raw = String(pathOrUrl ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;

  const uploadOrigin = uploadOriginFromApiBase(apiBaseUrl);

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      // DB rows saved in dev often store http://localhost:5050/uploads/...
      if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
        return `${uploadOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return raw;
    }
    return raw;
  }

  let path = raw.startsWith("/") ? raw : `/${raw}`;
  if (path.startsWith("/api/")) {
    path = path.slice(4);
  }
  return `${uploadOrigin}${path}`;
}
